import type { FileId } from "@excalidraw/element/types";
import type { BinaryFileData } from "@excalidraw/excalidraw/types";

import type { LocalData } from "./LocalData";

import type Portal from "../collab/Portal";
import type { FileManager } from "./FileManager";

type LocalFileManager = typeof LocalData.fileStorage;

/**
 * Interface for bridging between local storage (IndexedDB) and collaboration storage (HTTP/Firebase)
 * This solves the core problem where library images are stored locally but need to be accessible
 * to collaborators through the collaboration storage backend.
 */
export interface StorageBridge {
  /**
   * Sync files from IndexedDB to collaboration storage when entering collaboration
   * or when importing library files while already in collaboration mode
   */
  syncLocalFilesToCollaboration(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }>;

  /**
   * Sync files from collaboration storage to IndexedDB for offline access
   * This ensures library files are available even when offline
   */
  syncCollaborationFilesToLocal(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }>;

  /**
   * Ensure files are available in collaboration storage, syncing from IndexedDB if needed
   * This is the main method used during canvas rendering to ensure file availability
   */
  ensureFilesInCollaboration(fileIds: FileId[]): Promise<BinaryFileData[]>;

  /**
   * Check if we're currently in collaboration mode
   */
  isInCollaborationMode(): boolean;

  /**
   * Get files from the most appropriate storage backend
   * Tries collaboration storage first, falls back to local storage
   */
  getFiles(fileIds: FileId[]): Promise<{
    loadedFiles: BinaryFileData[];
    erroredFiles: Map<FileId, true>;
  }>;
}

/**
 * Abstract base class for storage bridge implementations
 * Provides common functionality for different storage backends
 */
export abstract class BaseStorageBridge implements StorageBridge {
  constructor(
    protected portal: Portal,
    protected localFileManager: LocalFileManager,
    protected collaborationFileManager: FileManager,
  ) {}

  isInCollaborationMode(): boolean {
    return !!(this.portal.roomId && this.portal.roomKey && this.portal.socket);
  }

  abstract syncLocalFilesToCollaboration(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }>;

  abstract syncCollaborationFilesToLocal(fileIds: FileId[]): Promise<{
    syncedFiles: FileId[];
    erroredFiles: FileId[];
  }>;

  abstract ensureFilesInCollaboration(
    fileIds: FileId[],
  ): Promise<BinaryFileData[]>;

  /**
   * Default implementation: check local storage first, then collaboration storage.
   *
   * Local-first strategy avoids unnecessary HTTP round-trips for files that are
   * already cached in IndexedDB (e.g. on reload or re-join). Only files missing
   * locally are fetched from the HTTP backend.
   */
  async getFiles(fileIds: FileId[]): Promise<{
    loadedFiles: BinaryFileData[];
    erroredFiles: Map<FileId, true>;
  }> {
    if (!fileIds.length) {
      return { loadedFiles: [], erroredFiles: new Map() };
    }

    // Always try local storage first — fast, no network cost
    const localResult = await this.localFileManager.getFiles(fileIds);
    const loadedFiles: BinaryFileData[] = [...localResult.loadedFiles];
    let erroredFiles = localResult.erroredFiles;

    // In collaboration mode, fetch any files not found locally from the HTTP backend
    if (this.isInCollaborationMode()) {
      const missingFileIds = Array.from(erroredFiles.keys());
      if (missingFileIds.length > 0) {
        const collabResult = await this.collaborationFileManager.getFiles(
          missingFileIds,
        );

        loadedFiles.push(...collabResult.loadedFiles);

        // Update errored files (remove ones found in collab storage)
        erroredFiles = new Map(erroredFiles);
        collabResult.loadedFiles.forEach((file: BinaryFileData) => {
          erroredFiles.delete(file.id);
        });
      }
    }

    return { loadedFiles, erroredFiles };
  }
}
